import { Box, Flex, Text, useRadioGroup } from '@chakra-ui/react';
import { DiscountCode } from '@time-line/components/common';
import { Supply } from '@time-line/components/icons';
import { Price } from 'components/cart/Price';
import { FormatPrice } from 'components/localisation/FormatPrice';
import type { LegacyRef } from 'react';
import { useEffect, useState } from 'react';
import { useMeasure } from 'react-use';
import { DiscountClass } from 'types/cart/PromoCode';
import { ProductType } from 'types/product/Product';
import type { Variant } from 'types/product/Variant';

import { ProductFormRadio } from '../ProductFormRadio';
import { ProductPriceSaving } from '../ProductPriceSaving';
import type { BaseConfiguratorProps } from '.';
import { Wrapper } from './Wrapper';

interface OTPMultiVariantProps extends BaseConfiguratorProps {
  availableForSale: boolean;
  otpVariant: Variant;
  variants: Variant[];
  productType: ProductType;
}

export function OTPMultiVariant({
  variants,
  productType,
  showSavingsAsAmount,
  isDiscountHidden,
  promoCodes,
  ...props
}: OTPMultiVariantProps) {
  const [ref, { width }] = useMeasure();

  const availableVariants = variants.filter((item) => !!item.availableForSale);

  const selectedIndex = Math.max(
    0,
    availableVariants.findIndex((v) => v.id === props.selectedVariant?.id),
  );

  const defaultVariant = availableVariants[selectedIndex];
  const discountCodes =
    promoCodes
      ?.filter(({ discountClass }) => discountClass === DiscountClass.PRODUCT)
      .map(({ code }) => code) || [];

  const [selectedVariant, setSelectedVariant] =
    useState<Variant>(defaultVariant);

  const isHorizontalSpaceAvailable = width / 130 >= variants.length;

  useEffect(() => {
    setSelectedVariant(defaultVariant);
  }, [defaultVariant]);

  const handleSelect = (val: string) => {
    const variant = variants.find((v) => v.id === val);
    if (!variant) return;

    setSelectedVariant(variant);
    props.setSelectedVariant?.(variant);
  };

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'otpVariant',
    value: selectedVariant.id,
    onChange: handleSelect,
  });

  const group = getRootProps();

  return (
    <Wrapper
      className="product_otpMultiVariant"
      variant={selectedVariant}
      isDiscountHidden={isDiscountHidden}
      {...props}
    >
      <Box borderWidth={1} borderRadius="base" borderColor="blackAlpha.250">
        <Flex
          as="span"
          sx={{
            '--dot-size': 'scale(1)',
          }}
          width="100%"
          alignItems="center"
          py="2xs"
          px="xs"
          height="100%"
          minH={66}
          gap="2xs"
        >
          <Text as="h2" flex="1" size="title2" textAlign="left">
            One-time purchase
          </Text>

          <Flex flexDir="column" align="flex-end" ml="auto" gap="5xs">
            {selectedVariant.servings && selectedVariant.price && (
              <FormatPrice
                className="price--per-serving"
                size="title2"
                textAlign="right"
                amount={selectedVariant.price.amount / selectedVariant.servings}
                isCurrencyCodeHidden
                afterText={
                  <Text
                    as="span"
                    size="detail"
                    fontWeight="500"
                  >{` / serving`}</Text>
                }
              />
            )}

            {selectedVariant.price && (
              <Flex direction="row" gap="4xs" alignItems="center">
                {!isDiscountHidden &&
                  discountCodes &&
                  discountCodes.length === 1 && (
                    <DiscountCode size="sm">{discountCodes[0]}</DiscountCode>
                  )}

                <Price
                  className="price--comparison"
                  as="span"
                  size="detail"
                  gap="5xs"
                  subtotalAmount={
                    !isDiscountHidden
                      ? selectedVariant.compareAtPrice
                      : undefined
                  }
                  totalAmount={selectedVariant.price}
                  direction="row-reverse"
                />
              </Flex>
            )}
          </Flex>
        </Flex>

        <Box ref={ref as LegacyRef<HTMLDivElement>}>
          <Box pb="2xs" px="2xs">
            <Box
              display="grid"
              gridAutoFlow={isHorizontalSpaceAvailable ? 'column' : 'row'}
              gridGap="5xs"
              border={0}
              {...group}
              aria-label="One-time purchase options"
            >
              {variants.map((variant) => {
                const isChecked = selectedVariant.id === variant.id;
                const isDisabled =
                  !!('availableForSale' in variant) &&
                  !variant.availableForSale;
                const radio = getRadioProps({
                  value: variant.id,
                  isChecked,
                  isDisabled,
                });

                return (
                  <ProductFormRadio
                    key={variant.id}
                    isVertical={!isHorizontalSpaceAvailable}
                    {...radio}
                  >
                    <Flex
                      px={isHorizontalSpaceAvailable ? '4xs' : 'xs'}
                      direction={isHorizontalSpaceAvailable ? 'column' : 'row'}
                      gap={0}
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                      lineHeight={1}
                    >
                      <Text
                        _groupChecked={{ color: 'gray.600' }}
                        color={
                          variant.availableForSale
                            ? 'gray.600'
                            : 'blackAlpha.700'
                        }
                        size="detail"
                        fontWeight="medium"
                      >
                        {variant.title}
                      </Text>
                      {!isDiscountHidden &&
                        variant.availableForSale &&
                        variant.price &&
                        variant.servings &&
                        variant.compareAtPrice && (
                          <ProductPriceSaving
                            compareAtPrice={variant.compareAtPrice}
                            price={variant.price}
                            showAsAmount={showSavingsAsAmount}
                          />
                        )}
                      {!variant.availableForSale && (
                        <Text
                          as="span"
                          size="detail"
                          color="blackAlpha.500"
                          fontWeight="medium"
                        >
                          Sold out
                        </Text>
                      )}
                    </Flex>
                  </ProductFormRadio>
                );
              })}
            </Box>
            {productType === ProductType.nutrition && (
              <Box pt="sm">
                <Flex alignItems="center" gap="3xs">
                  <Supply boxSize="xs" />
                  <Text size="detail">
                    {variants.length === 1
                      ? 'Supply delivered once'
                      : `${selectedVariant.title || ''} delivered once`}
                  </Text>
                </Flex>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Wrapper>
  );
}
